import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, toRaw, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFilterStore } from 'Store/filterStore'
//Types
import type { Ref } from 'vue'
import { ImageReviewStates } from 'Modules/types/generic/image/ImageReviewStates'
import { ImageType } from 'Modules/types/pages/imageReview/imageType'
import { ImageFilters } from 'Modules/types/filterTypes'

export const useImagesStore = defineStore('ImageReview_Image', () => {
  //State
  const route = useRoute()
  const router = useRouter()
  const imagesData: Ref<Array<ImageType>> = ref([])
  const currentImageLoaded = ref(false)
  const currentImageUuid = ref<string | null>(null)
  const filterStore = useFilterStore()
  const { queryParamObj } = storeToRefs(filterStore);

  const isCompletedImagesHidden = computed<boolean>(() =>
    filterStore.filterState.image?.includes(ImageFilters.Completed)
  )

  //Getters
  const images = computed<Array<ImageType>>(() => {
    if (imagesData.value.length === 0) return []
    if (isCompletedImagesHidden.value === true)
      return imagesData.value?.filter((image: ImageType) => image.reviewState !== ImageReviewStates.Complete)
    return imagesData.value
  })
  const allImagesReviewed = computed<boolean>(() => {
    if (imagesData.value === null) return false
    return Array.from(images.value).every((i: ImageType) => i.reviewState === ImageReviewStates.Complete)
  })

  // Todo should be able to make computed based on queryParamObj, but was not reacting
watch(queryParamObj, (newVal) => {
  currentImageUuid.value = newVal?.inspectionImage?.[0] || null
}, { deep: true });


  const currentImage = computed<ImageType>(() => {
    return images.value.find((image: ImageType) => image?.imageUuid === currentImageUuid.value)
  })

  const currentImageIndex = computed<number>(() => {
    return imagesData.value.findIndex((image: ImageType) => image?.imageUuid === currentImageUuid.value)
  })

  //Actions
  function getImagesLength(): number {
    return images.value?.length || 0
  }
  function getImagesCompletedLength(): number {
    return images.value?.filter((image) => image.reviewState === ImageReviewStates.Complete)?.length || 0
  }
  function getNextImageUuid(): string | null {
    if (currentImageIndex.value !== images.value.length - 1) return images.value[currentImageIndex.value + 1]?.imageUuid
    else return null
  }
  function getPreviousImageUuid(): string | null {
    if (currentImageIndex.value !== 0) return images.value[currentImageIndex.value - 1]?.imageUuid
    else return null
  }
  function setImagesData(images: Array<ImageType>): void {
    imagesData.value = images
  }
  function setImageReviewState(imageUuid: string, newState: ImageReviewStates): void {
    const newImagesData = imagesData.value.map((image: ImageType) => {
      const rawImage = toRaw(image)
      if (rawImage.imageUuid === imageUuid) {
        return {
          ...rawImage,
          reviewState: newState,
        }
      }
      return image
    })
    imagesData.value = newImagesData
  }

  const setCurrentImageLoaded = (isLoaded: boolean) => currentImageLoaded.value = isLoaded

  const findNextIncomplete = (): ImageType | null => {
    if (!currentImage.value) return
    for (let index = currentImageIndex.value; index < imagesData.value.length; index++) {
      const img = images.value[index]
      if (img?.reviewState !== ImageReviewStates.Complete && img.imageUuid !== currentImageUuid.value) return img
    }
    return null
  }

  const findLastIncomplete = (): ImageType | null => {
    if (!currentImage.value) return
    for (let index = currentImageIndex.value; index >= 0; index--) {
      const img = imagesData.value[index]
      if (!img?.reviewState)
        if (img?.reviewState !== ImageReviewStates.Complete && img.imageUuid !== currentImageUuid.value) return img
    }
    return null
  }

  watch(currentImage, () => {
    //Handles case where current image is incomplete and user hides completed images
    if (currentImage.value === undefined) {
      const nextImage = findNextIncomplete() || findLastIncomplete()
      if (nextImage)
        router.replace({
          ...route,
          query: { ...route.query, inspectionImage: [nextImage.imageUuid] },
        })
    }
  })

  return {
    allImagesReviewed,
    currentImage,
    currentImageUuid,
    currentImageIndex,
    images,
    currentImageLoaded,
    getImagesLength,
    getImagesCompletedLength,
    getNextImageUuid,
    getPreviousImageUuid,
    setCurrentImageLoaded,
    setImagesData,
    setImageReviewState,
    findNextIncomplete,
    findLastIncomplete,
  }
})
